/**
 * MilestoneCard - Displays a single milestone with its state badge
 *
 * Shows: title, description, developer name, delivery time, role,
 * skills, proficiency, availability, budget, and state badge.
 *
 * Mirrors the EJS partial milestones/info/_showMilestone.ejs.
 */

import { MilestoneStatusBadge } from '@components/features/projects/MilestoneStatusBadge';
import { cn } from '@lib/cn';
import { budgetPlanckToHuman } from '@lib/dusdUnits';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import type { Milestone } from '@/types/index';

interface MilestoneCardProps {
  milestone: Milestone;
  index: number;
  allDeliveryTimes?: Array<{ id?: number; description: string }>;
  className?: string;
  isLast?: boolean;
}

/**
 * Resolves the delivery time description from the enum index.
 */
function getDeliveryTimeLabel(
  deliveryTime: string | number | undefined,
  allDeliveryTimes?: Array<{ id?: number; description: string }>,
): string {
  if (deliveryTime === undefined || deliveryTime === null) {
    return 'Delivery Time Pending';
  }
  if (allDeliveryTimes && typeof deliveryTime === 'number') {
    return allDeliveryTimes[deliveryTime]?.description ?? 'Delivery Time Pending';
  }
  if (typeof deliveryTime === 'string') {
    return deliveryTime;
  }
  return 'Delivery Time Pending';
}

/**
 * Formats the availability for display.
 */
function formatAvailability(milestone: Milestone): string | null {
  if (milestone.availability) {
    if (milestone.availability === 'fulltime') return 'Full Time';
    if (milestone.availability === 'parttime') return 'Part Time';
    if (milestone.availability === 'hourly') {
      return `${milestone.neededHours ?? 0} Hours/Week`;
    }
    // Handle enum-style values
    if (milestone.availability === 'FullTime') return 'Full Time';
    if (milestone.availability === 'PartTime') return 'Part Time';
    if (milestone.availability === 'WeeklyHours') {
      return `${milestone.neededHours ?? 0} Hours/Week`;
    }
    return milestone.availability;
  }
  return null;
}

export function MilestoneCard({
  milestone,
  index,
  allDeliveryTimes,
  className,
  isLast,
}: MilestoneCardProps) {
  const { workerRole, currency } = useDaoConfig();
  const deliveryLabel = getDeliveryTimeLabel(milestone.deliveryTime, allDeliveryTimes);
  const availability = formatAvailability(milestone);
  const budgetDisplay =
    milestone.budget !== undefined && milestone.budget !== null
      ? `${budgetPlanckToHuman(milestone.budget).toLocaleString('en-US')} ${currency.symbol}`
      : 'Budget Pending';

  return (
    <div
      className={cn(
        'h-fit w-full p-0 bg-transparent border-0 flex gap-[--spacing-8] text-left',
        className,
      )}
    >
      {/* Progress indicator dot */}
      <div className="pt-2 flex flex-col items-center">
        <div className="z-10 h-1.5 w-1.5 rounded-full bg-transparent outline outline-[6px] outline-offset-1 outline-[--base-fill-3]" />
        <div className={cn('h-full w-px', isLast ? 'bg-transparent' : 'bg-[--base-border]')} />
      </div>
      <div className="w-full p-0 pb-[--spacing-8] space-y-[--spacing-5]">
        {/* Header row: number, title, budget, state */}
        <div className="w-full flex items-center gap-[--spacing-5] flex-wrap">
          <h4 className="flex-1 text-base font-normal text-[--text-dark-tertiary]">
            Milestone {index + 1}
          </h4>
          <p className="text-xs">[{budgetDisplay}]</p>
          <p className="text-xs">Delivery: {deliveryLabel}</p>
        </div>
        {/* Header row: number, title, budget, state */}
        <div className="flex items-baseline justify-between gap-4 mb-3">
          <div className="flex items-start gap-[--spacing-5] min-w-0">
            <h4 className="text-base font-bold text-[--text-dark-primary]">{milestone.title}</h4>
          </div>

          <MilestoneStatusBadge milestone={milestone} className="hidden" />
        </div>

        {/* Description */}
        {milestone.description && (
          <p className="text-base font-medium text-[--text-dark-secondary]">
            {milestone.description}
          </p>
        )}

        {/* Role / Skills / Proficiency / Availability */}
        {(milestone.role || milestone.skills?.length || milestone.proficiency || availability) && (
          <div className="my-3 flex flex-wrap items-center gap-2">
            {milestone.role && <span className="tag">{milestone.role}</span>}
            {milestone.proficiency && <span className="tag">{milestone.proficiency}</span>}
            {milestone.skills?.map((skill) => (
              <span key={skill} className="tag">
                {skill}
              </span>
            ))}
            {availability && <span className="tag">{availability}</span>}
          </div>
        )}

        {/* Developer assignment */}
        <div className="flex items-center gap-2 text-xs text-[--text-dark-secondary]">
          {milestone.developerId ? (
            <>
              <span className="h-[32px] aspect-square rounded-full border border-[--base-border] overflow-hidden flex items-center justify-center">
                <img
                  className="w-full h-full object-cover"
                  src={`/developers/${milestone.developerId}/attachment`}
                  alt={milestone.developer?.name ?? workerRole}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/images/none.png';
                  }}
                />
              </span>
              <span className="text-[--text-dark-tertiary]">
                {milestone.developer?.name ?? `Assigned ${workerRole}`}
              </span>
            </>
          ) : (
            <>
              <span className="h-[32px] aspect-square rounded-full border border-[--base-border] flex items-center justify-center">
                <i className="ri-user-smile-line text-[20px] text-[--base-fill-5]" />
              </span>
              <span className="italic">Unassigned</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
